<script setup>
import { Validator } from '@vueform/vueform';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { storeToRefs } from 'pinia';
import { RRule } from 'rrule';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { generateUID } from '~/project-management/utils/pm-helper.utils';

const props = defineProps({
  scheduledExports: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'save']);
dayjs.extend(utc);
dayjs.extend(timezone);

const { $t, auth_store, common_store } = useCommonImports();

const bi_store = useBiStore();
const { selected_dashboard, selected_dashboard_details, get_applied_filters_for_dashboard, get_global_filters_with_applied_filters } = storeToRefs(bi_store);

const form$ = ref(null);

const state = reactive({
  is_loading: false,
});

const checkEmails = class extends Validator {
  get message() {
    return 'Please enter valid email address';
  }

  check(value) {
    if (!value || !Array.isArray(value))
      return true;

    return value.every((item) => {
      if (item?.uid && item?.name && item.uid === item.name) {
        const emailFormat = /^[\w.!#$%&'*+/=?^`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i;
        return emailFormat.test(item.name);
      }
      return true;
    });
  }
};

const frequency_items = [
  { label: $t('Daily'), value: 'daily' },
  { label: $t('Weekly'), value: 'weekly' },
  { label: $t('Monthly'), value: 'monthly' },
];

const weekday_items = [
  { label: $t('Mon'), value: 'mon' },
  { label: $t('Tue'), value: 'tue' },
  { label: $t('Wed'), value: 'wed' },
  { label: $t('Thu'), value: 'thu' },
  { label: $t('Fri'), value: 'fri' },
  { label: $t('Sat'), value: 'sat' },
  { label: $t('Sun'), value: 'sun' },
];

const day_of_month_items = [
  { label: $t('First day'), value: 'first' },
  { label: $t('Last day'), value: 'last' },
  { label: $t('1st'), value: 1 },
  { label: $t('2nd'), value: 2 },
  { label: $t('3rd'), value: 3 },
  { label: $t('4th'), value: 4 },
];

const weekday_for_monthly_items = [
  { label: $t('Monday'), value: 'monday' },
  { label: $t('Tuesday'), value: 'tuesday' },
  { label: $t('Wednesday'), value: 'wednesday' },
  { label: $t('Thursday'), value: 'thursday' },
  { label: $t('Friday'), value: 'friday' },
  { label: $t('Saturday'), value: 'saturday' },
  { label: $t('Sunday'), value: 'sunday' },
];

const time_list = (() => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hour12 = hour % 12 || 12;
      const am_pm = hour < 12 ? $t('AM') : $t('PM');
      const timeStr = `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${am_pm}`;
      times.push({
        label: timeStr,
        value: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
      });
    }
  }
  return times;
})();

const DAY_TO_INT_MAP = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

// RRule uses: 0=Monday, 1=Tuesday, 2=Wednesday, 3=Thursday, 4=Friday, 5=Saturday, 6=Sunday
const INT_TO_DAY_MAP = {
  0: 'mon',
  1: 'tue',
  2: 'wed',
  3: 'thu',
  4: 'fri',
  5: 'sat',
  6: 'sun',
};

const INT_TO_MONTHLY_DAY_MAP = {
  0: 'monday',
  1: 'tuesday',
  2: 'wednesday',
  3: 'thursday',
  4: 'friday',
  5: 'saturday',
  6: 'sunday',
};

const INT_TO_RRULE_MAP = [
  RRule.SU,
  RRule.MO,
  RRule.TU,
  RRule.WE,
  RRule.TH,
  RRule.FR,
  RRule.SA,
];

function getWeekdayInt(val) {
  return DAY_TO_INT_MAP[val.toLowerCase()] ?? 0;
}

function generateRRule(export_data) {
  const { frequency, weekdays, day_of_month, weekday, time } = export_data;
  const user_timezone = auth_store.logged_in_user_details?.timezone || dayjs.tz.guess();

  const local_dt = dayjs.tz(`${dayjs().format('YYYY-MM-DD')} ${time}:00`, user_timezone);

  const utc_dt = local_dt.utc();

  // This handles crossing midnight (e.g. Tue IST -> Mon UTC)
  const shift = utc_dt.day() - local_dt.day();

  const rrule_config = {
    dtstart: utc_dt.toDate(),
  };

  if (frequency === 'daily') {
    rrule_config.freq = RRule.DAILY;
  }
  else if (frequency === 'weekly') {
    rrule_config.freq = RRule.WEEKLY;

    if (weekdays && weekdays.length > 0) {
      rrule_config.byweekday = weekdays.map((day_str) => {
        const day_idx = getWeekdayInt(day_str);

        let shifted_idx = day_idx + shift;

        if (shifted_idx < 0)
          shifted_idx += 7;
        if (shifted_idx > 6)
          shifted_idx -= 7;

        return INT_TO_RRULE_MAP[shifted_idx];
      });
    }
  }
  else if (frequency === 'monthly') {
    rrule_config.freq = RRule.MONTHLY;

    if (day_of_month === 'first') {
      // Logic for shifting 'first day' is complex (might become last day of prev month).
      // For simplicity in this context, we usually stick to day 1 unless strict accuracy
      // is required for 'first day' crossing months, which is rare for exports.
      // Ideally, simple monthly exports align to the date number.
      rrule_config.bymonthday = 1;
    }
    else if (day_of_month === 'last') {
      rrule_config.bymonthday = -1;
    }
    else if (typeof day_of_month === 'number' && weekday) {
      const day_idx = getWeekdayInt(weekday);

      // We apply the same shift logic to the weekday component of monthly
      let shifted_idx = day_idx + shift;
      if (shifted_idx < 0)
        shifted_idx += 7;
      if (shifted_idx > 6)
        shifted_idx -= 7;

      const rrule_weekday = INT_TO_RRULE_MAP[shifted_idx];
      rrule_config.byweekday = rrule_weekday.nth(day_of_month);
    }
  }

  return new RRule(rrule_config).toString();
}

function parseRRuleToFormData(rrule_string) {
  const { options: rrule_options } = RRule.fromString(rrule_string);
  const user_timezone = auth_store.logged_in_user_details?.timezone || dayjs.tz.guess();

  const utc_dt = dayjs.utc(rrule_options.dtstart);

  const local_dt = utc_dt.tz(user_timezone);

  const time = local_dt.format('HH:mm');

  // Calculate Reverse Shift (Local - UTC) to restore UI selection
  const shift = local_dt.day() - utc_dt.day();

  const form_data = {
    time,
  };

  if (rrule_options.freq === RRule.DAILY) {
    form_data.frequency = 'daily';
  }
  else if (rrule_options.freq === RRule.WEEKLY) {
    form_data.frequency = 'weekly';

    form_data.weekdays = (rrule_options.byweekday || []).map((day_int) => {
      const original_idx = day_int;

      let shifted_idx = original_idx + shift;

      if (shifted_idx < 0)
        shifted_idx += 7;
      if (shifted_idx > 6)
        shifted_idx -= 7;

      return INT_TO_DAY_MAP[shifted_idx];
    });
  }
  else if (rrule_options.freq === RRule.MONTHLY) {
    form_data.frequency = 'monthly';

    if (rrule_options.bynmonthday && rrule_options.bynmonthday.length > 0) {
      const day = rrule_options.bynmonthday[0];
      if (day === 1)
        form_data.day_of_month = 'first';
      else if (day === -1)
        form_data.day_of_month = 'last';
    }
    else if (rrule_options.bynweekday && rrule_options.bynweekday.length > 0) {
      const [weekday_num, nth] = rrule_options.bynweekday[0];

      form_data.day_of_month = nth;

      let shifted_idx = weekday_num + shift;
      if (shifted_idx < 0)
        shifted_idx += 7;
      if (shifted_idx > 6)
        shifted_idx -= 7;

      form_data.weekday = INT_TO_MONTHLY_DAY_MAP[shifted_idx];
    }
  }

  return form_data;
}

async function onSave() {
  const form_data = form$.value.data;

  const scheduled_exports = (form_data.scheduled_exports || []).map((export_item) => {
    const uid = export_item.uid || generateUID();

    const to = (export_item.members || []).map((member) => {
      if (typeof member === 'string')
        return member;
      if (member.uid === member.name)
        return member.name;
      return member.uid;
    });

    const rrule = generateRRule(export_item);

    let filters = [];
    if (export_item.uid && export_item.filters) {
      filters = export_item.filters;
    }
    else if (export_item.use_filters) {
      filters = get_applied_filters_for_dashboard.value;
    }

    return {
      uid,
      name: export_item.name || null,
      to,
      rrule,
      filters,
      send_single_email: export_item.send_single_email || false,
    };
  });

  const payload = {
    scheduled_exports,
  };

  await bi_store.updateDashboard(selected_dashboard.value, payload);
  emit('close');
}

function onFormMounted() {
  if (props.scheduledExports && props.scheduledExports.length > 0) {
    const loaded_exports = props.scheduledExports.map((export_config) => {
      const parsed_rrule = parseRRuleToFormData(export_config.rrule);

      return {
        uid: export_config.uid,
        name: export_config.name,
        members: export_config.to.map((item) => {
          if (common_store.is_type_user(item)) {
            return { uid: item, name: common_store.get_user(item)?.email, member: true };
          }
          else if (common_store.is_type_team(item)) {
            return { uid: item, name: common_store.get_team(item)?.name, member: false };
          }
          else {
            return { uid: item, name: item };
          }
        }),
        ...parsed_rrule,
        filters: export_config.filters,
        send_single_email: export_config.send_single_email || false,
      };
    });

    form$.value.update({
      scheduled_exports: loaded_exports,
    });
  }
}

function hasSavedFilters(index) {
  const form_data = form$.value?.data;
  if (!form_data?.scheduled_exports?.[index])
    return false;
  const export_item = form_data.scheduled_exports[index];
  return export_item.uid && export_item.filters && Object.keys(export_item.filters).length > 0;
}

function isNewConfig(index) {
  const form_data = form$.value?.data;
  if (!form_data?.scheduled_exports?.[index])
    return true;
  const export_item = form_data.scheduled_exports[index];
  return !export_item.uid;
}

function applyFiltersToCurrentDashboard(index) {
  const form_data = form$.value?.data;
  if (!form_data?.scheduled_exports?.[index])
    return;
  const export_item = form_data.scheduled_exports[index];
  if (!export_item.filters || Object.keys(export_item.filters).length === 0)
    return;

  bi_store.clearAllAppliedGlobalFilters();
  Object.keys(export_item.filters).forEach((filter_key) => {
    bi_store.updateAppliedGlobalFilter(filter_key, export_item.filters[filter_key]);
  });
  emit('close');
}

function showSingleEmailCheckbox(index) {
  const members = form$.value?.el$(`scheduled_exports.${index}.members`)?.value;
  if (!members || !Array.isArray(members) || members.length === 0)
    return false;
  if (members.length > 1)
    return true;
  return common_store.is_type_team(members[0]?.uid);
}
</script>

<template>
  <HawkModalContainer id="bi-schedule-exports-modal" content_class="!w-[800px]">
    <Vueform
      ref="form$"
      size="sm"
      class="max-w-[800px]"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :endpoint="onSave"
      :display-errors="false"
      @mounted="onFormMounted"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            <div class="flex flex-col justify-start">
              {{ $t('Schedule exports') }}
            </div>
          </template>
          <template #subtitle>
            <div class="flex flex-col justify-start">
              {{ $t('Create and manage automated dashboard exports') }}
            </div>
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <ListElement
            name="scheduled_exports"
            :controls="{ add: false, remove: false, sort: false }"
            :add-classes="{
              ListElement: {
                add: '!bg-white !border-white !text-primary-700 hover:!scale-100 !mt-0 -ml-1',
                remove: '!-right-4',
              },
            }"
            :remove-classes="{
              ListElement: {
                remove: 'left-0',
              },
            }"
            :columns="{
              default: { container: 12, label: 12, wrapper: 12 },
              sm: { container: 12, label: 12, wrapper: 12 },
              md: { container: 12, label: 12, wrapper: 12 },
            }"
            :initial="0"
          >
            <template #default="{ index }">
              <ObjectElement :name="index" class="border rounded-lg px-4 py-4 relative mb-3">
                <div class="col-span-12">
                  <div class="pr-3">
                    <TextElement
                      name="name"
                      :label="$t('Name')"
                      :placeholder="selected_dashboard_details.name"
                      :presets="['underline']"
                      :add-classes="{
                        TextElement: {
                          inputContainer: '!h-9 -ml-2',
                        },
                      }"
                      class="mb-3"
                    />
                    <div>
                      <div class="absolute top-4 right-4 z-10">
                        <IconHawkTrashOne
                          class="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer"
                          @click="form$.el$(`scheduled_exports`).remove(index)"
                        />
                      </div>
                      <HawkAssigneeInput
                        :options="{
                          name: 'members',
                          label: $t('Members'),
                          placeholder: $t('Select members'),
                          presets: ['underline'],
                          has_teams: true,
                          allow_create: true,
                          create: true,
                          rules: ['required', checkEmails],
                          appendTo: '#bi-schedule-exports-modal',
                          addClasses: {
                            TagsElement: {
                              select: {
                                container: '-ml-2',
                              },
                            },
                          },
                        }"
                        :multi="true"
                      />
                      <div v-if="showSingleEmailCheckbox(index)" class="flex mt-2">
                        <div class="shrink-0 w-1/3" />
                        <CheckboxElement
                          name="send_single_email"
                          :columns="{ default: 12, sm: 12, md: 12 }"
                        >
                          {{ $t('Send single email to all recipients') }}
                        </CheckboxElement>
                      </div>
                    </div>
                  </div>

                  <div class="flex items-end gap-2 mt-4">
                    <span class="text-sm text-gray-500 mb-2">{{ $t('Schedule') }}</span>

                    <SelectElement
                      name="frequency"
                      :items="frequency_items"
                      :native="false"
                      :can-clear="false"
                      :can-deselect="false"
                      default="daily"
                      :presets="['underline']"
                      class="w-28"
                      append-to="#bi-schedule-exports-modal"
                    />

                    <template v-if="form$.el$(`scheduled_exports.${index}.frequency`)?.value === 'weekly'">
                      <span class="text-sm text-gray-500 mb-2">{{ $t('on') }}</span>
                      <TagsElement
                        name="weekdays"
                        :items="weekday_items"
                        :native="false"
                        :close-on-select="false"
                        :presets="['underline', 'supress_errors']"
                        :columns="{ default: 12, sm: 12, md: 12 }"
                        class="flex-1 !w-32"
                        append-to="#bi-schedule-exports-modal"
                        :rules="['required']"
                      />
                    </template>

                    <template v-if="form$.el$(`scheduled_exports.${index}.frequency`)?.value === 'monthly'">
                      <span class="text-sm text-gray-500 mb-2">{{ $t('on') }}</span>
                      <SelectElement
                        name="day_of_month"
                        :items="day_of_month_items"
                        :native="false"
                        :can-clear="false"
                        :can-deselect="false"
                        default="first"
                        :presets="['underline']"
                        :columns="{ default: 12, sm: 12, md: 12 }"
                        class="w-32"
                        append-to="#bi-schedule-exports-modal"
                      />
                      <SelectElement
                        v-if="form$.el$(`scheduled_exports.${index}.day_of_month`)?.value !== 'first' && form$.el$(`scheduled_exports.${index}.day_of_month`)?.value !== 'last'"
                        name="weekday"
                        :items="weekday_for_monthly_items"
                        :native="false"
                        :can-clear="false"
                        :can-deselect="false"
                        default="monday"
                        :presets="['underline']"
                        :columns="{ default: 12, sm: 12, md: 12 }"
                        class="w-36"
                        append-to="#bi-schedule-exports-modal"
                      />
                    </template>

                    <span class="text-sm text-gray-500 mb-2">{{ $t('at') }}</span>
                    <SelectElement
                      name="time"
                      :items="time_list"
                      :native="false"
                      :can-clear="false"
                      :can-deselect="false"
                      :search="true"
                      default="09:00"
                      :presets="['underline']"
                      :columns="{ default: 12, sm: 12, md: 12 }"
                      class="w-32"
                      append-to="#bi-schedule-exports-modal"
                    />
                  </div>

                  <CheckboxElement
                    v-if="isNewConfig(index) && Object.keys(get_applied_filters_for_dashboard).length"
                    name="use_filters"
                    :columns="{ default: 12, sm: 12, md: 12 }"
                    class="mt-4"
                  >
                    {{ $t('Use current dashboard filters while exporting') }}
                    <IconHawkInfoCircle
                      v-tippy="{
                        content: `${$t('Applied filters')}: ${get_global_filters_with_applied_filters.map(filter => filter.general.filter_name).join(', ')}`,
                        placement: 'right',
                      }"
                      class="inline ml-1 cursor-pointer h-4 w-4 text-gray-400"
                    />
                  </CheckboxElement>
                  <HiddenElement name="uid" />
                  <HiddenElement name="filters" />

                  <div
                    v-if="hasSavedFilters(index)"
                    class="mt-4 text-sm text-gray-600"
                  >
                    <span class="font-medium">{{ $t('Note') }}:</span>
                    {{ $t('This export has filters applied.') }}
                    <span
                      class="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                      @click.prevent="applyFiltersToCurrentDashboard(index)"
                    >
                      {{ $t('Apply') }}
                    </span>
                    {{ $t('to current dashboard to view them.') }}
                  </div>
                </div>
              </ObjectElement>
            </template>
          </ListElement>
          <HawkButton type="text" @click="form$.el$(`scheduled_exports`).add()">
            <IconHawkPlus />
            {{ $t('Schedule export') }}
          </HawkButton>
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end w-full col-span-full">
              <HawkButton
                class="mr-4"
                type="outlined"
                @click="emit('close')"
              >
                {{ $t('Cancel') }}
              </HawkButton>
              <ButtonElement
                name="save"
                :loading="state.is_loading"
                submits
              >
                {{ $t('Save') }}
              </ButtonElement>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
